import { useEffect, useState } from "react"
import HistoryBoard from "./HistoryBoard"
import HistoryPlayerItem from "./HistoryPlayerItem"
import TradeEventCard from "./TradeEventCard"
import ReactJson from "react-json-view"

// Custom slider styles
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #1f2937;
    margin-top: -6px;
  }
  
  .slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #1f2937;
  }
  
  .slider::-webkit-slider-track {
    background: #374151;
    border-radius: 8px;
    height: 8px;
  }
  
  .slider::-moz-range-track {
    background: #374151;
    border-radius: 8px;
    height: 8px;
  }
  
  .slider {
    height: 8px;
    background: #374151;
    border-radius: 8px;
    outline: none;
  }
`

interface TimelineTabProps {
  gameHistory: IMinifiedGameHistory
}

const TimelineTab: React.FC<TimelineTabProps> = ({ gameHistory }) => {
  const [currentEventIndex, setCurrentEventIndex] = useState(0)
  const currentSituation = gameHistory.history[currentEventIndex].situation
  const currentEvent = gameHistory.history[currentEventIndex].event

  const maxIndex = Math.max(0, gameHistory.history.length - 1)
  // Indices of events where a trade was offered — used to mark the slider.
  const tradeOfferedIndices = gameHistory.history.reduce<number[]>(
    (acc, item, i) => {
      const event = item.event
      if (event.type === "trade" && event.status === "offered") {
        const offeringPlayer = item.situation.players.find(
          (p) => p.order === event.trade.offeringPlayerOrder
        )
        if (offeringPlayer && offeringPlayer.money > 0) {
          acc.push(i)
        }
      }
      return acc
    },
    []
  )

  // Left/Right arrows step through events. Holding a key relies on the browser's
  // native key-repeat (keydown fires repeatedly) for continuous movement.
  useEffect(() => {
    const max = gameHistory.history.length - 1
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't hijack arrows while focused on a form control (e.g. the slider,
      // which already steps on its own, or the JSON viewer's inputs).
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        setCurrentEventIndex((i) => Math.max(0, i - 1))
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        setCurrentEventIndex((i) => Math.min(max, i + 1))
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [gameHistory.history.length])

  return (
    <div className="space-y-6">
      <style>{sliderStyles}</style>

      {/* Timeline Slider */}
      <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">
            Event {currentEventIndex + 1} of {gameHistory.history.length}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() =>
                setCurrentEventIndex(Math.max(0, currentEventIndex - 1))
              }
              disabled={currentEventIndex === 0}
              className="px-3 py-1 bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-500"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentEventIndex(
                  Math.min(
                    gameHistory.history.length - 1,
                    currentEventIndex + 1
                  )
                )
              }
              disabled={currentEventIndex === gameHistory.history.length - 1}
              className="px-3 py-1 bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-500"
            >
              Next
            </button>
          </div>
        </div>

        {/* Slider */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="range"
              min="0"
              max={maxIndex}
              value={currentEventIndex}
              onChange={(e) => setCurrentEventIndex(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
            />
            {/* Trade-offered markers overlaid on the slider track. The thumb is
                20px wide, so its center travels between 10px and (100% - 10px);
                align markers to that same range. */}
            {maxIndex > 0 &&
              tradeOfferedIndices.map((i) => (
                <div
                  key={i}
                  title={`Trade offered (Event ${i + 1})`}
                  className="pointer-events-none absolute top-1/2 z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-900 bg-yellow-400"
                  style={{
                    left: `calc(${i / maxIndex} * (100% - 20px) + 10px)`,
                  }}
                />
              ))}
          </div>
          {tradeOfferedIndices.length > 0 && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
              <span className="inline-block h-3 w-3 rounded-full border border-gray-900 bg-yellow-400" />
              Trade offered
            </div>
          )}
        </div>

        {/* Current Event Display */}
        {gameHistory.history[currentEventIndex] && (
          <div className="flex gap-8">
            <div className="flex-1">
              <HistoryBoard
                game={{
                  ...gameHistory.constantData.game,
                  //@ts-ignore
                  situation: {
                    ...gameHistory.constantData.game.situation,
                    ...gameHistory.history[currentEventIndex].situation,
                  } as IGameSituation,
                }}
              />
            </div>
            <div className="flex-1 bg-gray-900/50 border border-gray-600 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-gray-400">
                  Event {currentEventIndex + 1}
                </span>
                <span className="text-sm text-gray-400"></span>
              </div>

              {/* Game State Info */}
              <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium text-lg">
                    Turns Left:{" "}
                    {gameHistory.history[currentEventIndex].situation.turnsLeft}
                  </span>
                  <span className="text-white font-medium text-lg">
                    Money on Table: $
                    {
                      gameHistory.history[currentEventIndex].situation
                        .moneyOnTable
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium text-lg">
                    Turn:{" "}
                    {
                      currentSituation.players.find(
                        (item) => item.id === currentSituation.turn
                      )?.order
                    }
                  </span>
                </div>
              </div>

              {/* Trade Event Details */}
              {currentEvent.type === "trade" && (
                <div className="mb-6">
                  <TradeEventCard
                    trade={currentEvent.trade}
                    status={currentEvent.status}
                    situation={currentSituation}
                    constantData={gameHistory.constantData}
                  />
                </div>
              )}

              {/* Players List */}
              <div className="space-y-6">
                <ReactJson
                  theme="codeschool"
                  style={{
                    backgroundColor: "transparent",
                    color: "white",
                    fontSize: "18px",
                  }}
                  src={gameHistory.history[currentEventIndex].event}
                  collapsed={1}
                />
                <br />
                <ReactJson
                  theme="codeschool"
                  style={{
                    backgroundColor: "transparent",
                    color: "white",
                    fontSize: "18px",
                  }}
                  src={gameHistory.history[currentEventIndex].situation}
                  collapsed={1}
                />

                <h5 className="text-white font-medium text-xl mb-4">Players</h5>
                {gameHistory.history[
                  currentEventIndex
                ]?.situation?.players?.map((player) => {
                  // Find the corresponding user data from constantData
                  const userData = gameHistory.constantData.players.find(
                    (user) => user.id === player.id
                  ) as IUser | undefined

                  // Convert minified player to full IPlayer format
                  const fullPlayer: IPlayer = {
                    ...player,
                    money: player.money,
                    properties: player.properties,
                    position: player.position,
                    id: player.id,
                    order: player.order,
                    isBankrupt: player.isBankrupt,
                    canBuyPropertiesWithoutAuction:
                      player.canBuyPropertiesWithoutAuction,
                    cards: player.cards.map((cardId) => ({
                      id: cardId,
                      cardId,
                      playable: false,
                    })),
                    stock: player.stock,
                    stockMultiplier: player.stockMultiplier,
                    turnsBeingBankrupt: player.turnsBeingBankrupt,
                    // Add required IPlayer properties
                    savedFromRent: false,
                    pieceId:
                      gameHistory.constantData.players.find(
                        (p) => p.id === player.id
                      )?.pieceId || "piece-1", // Default piece ID
                    eliminated: false,
                    rollHistory: [],
                    inJail: false,
                    inJailTurns: 0,
                    jailFreeCards: [],
                    boughtPropertiesWithoutAuction: 0,
                    role:
                      gameHistory.constantData.pickedRoles[player.id] || "none",
                    bot: false,
                    cardsPlayed: 0,
                  }

                  return (
                    <div
                      key={player.id}
                      className="bg-gray-800/30 border border-gray-600 rounded-lg p-4"
                    >
                      <HistoryPlayerItem
                        game={gameHistory.constantData.game}
                        player={fullPlayer}
                        user={
                          userData || {
                            username: player.id,
                            email: player.id,
                            password: "",
                            isVerified: true,
                            otp: "",
                            otpTries: 0,
                            resentOtpTimes: 0,
                            stats: {
                              rating: 0,
                              gamesPlayed: 0,
                              gamesWon: 0,
                            },
                            role: "none",
                            profileImage: "",
                          }
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TimelineTab
